import {
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnInit,
  ViewChild
} from '@angular/core';
import { AgmMap, MapsAPILoader } from '@agm/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Location, LocationPin } from '@ml/locations/types';

declare const google: any;

@Component({
  selector: 'ml-customer-location-modal',
  template: `
    <div class="container" fxLayout="column" fxLayoutGap="16px">
      <div class="mat-title">
        {{ (disable ? 'location' : 'location-title') | translate | titlecase }}
      </div>
      <div class="mat-subheading-2" *ngIf="!disable">
        {{ 'location-description' | translate }}
      </div>

      <div fxLayout="column" fxLayout.sm="row" fxLayoutGap.sm="16px">
        <mat-form-field class="full-width">
          <mat-label>{{ 'name' | translate | titlecase }}</mat-label>
          <input matInput [formControl]="inputForm" />
          <mat-error *ngIf="inputForm.hasError('required')">
            {{ 'name' | translate | titlecase }} {{ 'is-required' | translate }}
          </mat-error>
        </mat-form-field>

        <mat-form-field class="full-width">
          <mat-label>{{ 'search' | translate | titlecase }}</mat-label>
          <input
            [disabled]="disable"
            matInput
            (keydown.enter)="$event.preventDefault()"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="off"
            type="text"
            #search
          />
        </mat-form-field>
      </div>
      <agm-map
        [latitude]="startLatitude"
        [longitude]="startLongitude"
        [zoomControl]="true"
        [mapDraggable]="true"
        [disableDefaultUI]="true"
        [zoom]="START_ZOOM"
        [mapTypeId]="'hybrid'"
        #agmMap
        (mapReady)="onMapReady($event)"
      >
      </agm-map>

      <div
        *ngIf="oneTimeToggle"
        fxLayout="row"
        fxLayoutAlign="start center"
        fxLayoutGap="8px"
      >
        <mat-slide-toggle
          [formControl]="oneTimeLocationControl"
        ></mat-slide-toggle>
        <div
          fxLayout="row"
          fxLayoutAlign="start center"
          fxLayoutGap="8px"
          class="info-locations"
        >
          <div>
            <mat-icon svgIcon="info"></mat-icon>
          </div>
          <div>
            <b>{{ 'notice' | translate }}:</b>
            {{ 'info-toggle-location' | translate }}
          </div>
        </div>
      </div>

      <ml-customer-location-pin
        [pins]="pins"
        [disable]="disable"
        (primary)="onPrimaryPin($event)"
        (remove)="onRemovePin($event)"
      ></ml-customer-location-pin>

      <div fxLayout="row" fxLayoutAlign="flex-end center">
        <button mat-button (click)="onCancel()">
          {{ 'cancel' | translate | uppercase }}
        </button>
        <button *ngIf="!disable" mat-button (click)="onClear()">
          {{ 'clear' | translate | uppercase }}
        </button>
        <button
          *ngIf="!disable"
          mat-button
          color="primary"
          (click)="onSubmit()"
          [disabled]="!valid"
        >
          {{ 'submit' | translate | uppercase }}
        </button>
      </div>
    </div>
  `,
  styleUrls: ['./customer-location-modal.component.scss']
})
export class CustomerLocationModalComponent implements OnInit {
  readonly START_ZOOM = 16;
  readonly POLYGON = 'polygon';
  readonly MARKER = 'marker';

  private managerOptions = {
    drawingMode: null,
    drawingControl: true,
    drawingControlOptions: {
      drawingModes: [this.POLYGON]
    },
    polygonOptions: {
      draggable: false,
      editable: true,
      strokeColor: '#1b5e20',
      strokeOpacity: 0.6,
      fillColor: '#1b5e20',
      fillOpacity: 0.35
    },
    markerOptions: {
      draggable: true
    }
  };

  @ViewChild(AgmMap, { static: true })
  public map: AgmMap;

  @ViewChild('search', { static: true })
  public searchElementRef: ElementRef;

  private drawingManager: any;
  private selectedShape: any;

  inputForm = new FormControl('', [Validators.required]);
  oneTimeToggle = false;
  oneTimeLocationControl = new FormControl(false);
  customerLocation: Location;
  startLatitude = 49.53336;
  startLongitude = 5.98863;
  disable: boolean;

  pins: LocationPin[];

  constructor(
    private mapsAPILoader: MapsAPILoader,
    private ngZone: NgZone,
    private dialogRef: MatDialogRef<CustomerLocationModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      customer: number;
      location: Location;
      disable: boolean;
      oneTimeToggle: boolean;
      fieldRequired: boolean;
    }
  ) {
    this.oneTimeToggle = this.data ? this.data.oneTimeToggle : false;
    if (this.data && this.data.location) {
      this.startLatitude = this.data.location.startPoint.lat;
      this.startLongitude = this.data.location.startPoint.long;
    }
    this.disable = this.data && this.data.disable;
    if (this.disable) {
      this.inputForm.disable();
      this.managerOptions.polygonOptions.editable = false;
      this.managerOptions.markerOptions.draggable = false;
    }
  }

  ngOnInit() {
    this.customerLocation = this.data ? this.data.location : null;
    this.pins = this.customerLocation ? [...this.customerLocation.pins] : [];

    this.mapsAPILoader.load().then(() => {
      const autocomplete = new google.maps.places.Autocomplete(
        this.searchElementRef.nativeElement
      );
      autocomplete.addListener('place_changed', () => {
        this.ngZone.run(() => {
          // get the place result
          const place = autocomplete.getPlace();

          // verify result
          if (place.geometry === undefined || place.geometry === null) {
            return;
          }

          // set latitude, longitude and zoom
          this.startLatitude = place.geometry.location.lat();
          this.startLongitude = place.geometry.location.lng();
        });
      });
    });
  }

  onRemovePin(index: number) {
    this.pins.forEach(value => value.marker.setMap(null));
    const pins = this.pins.filter((value, indexValue) => indexValue !== index);
    this.pins = this.getPins(pins);
  }

  onPrimaryPin(index: number) {
    this.pins.forEach(value => value.marker.setMap(null));
    const pins = this.pins.map((value, valueIndex) => ({
      ...value,
      primary: valueIndex === index
    }));
    this.pins = this.getPins(pins);
  }

  get valid() {
    const valid = this.inputForm.valid && this.pins.length > 0;
    if (this.data && this.data.fieldRequired) {
      return valid && !!this.selectedShape;
    }
    return valid;
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSubmit() {
    const location = new Location();
    location.name = this.inputForm.value;
    if (this.selectedShape && this.selectedShape.getPath()) {
      location.points = this.selectedShape
        .getPath()
        .getArray()
        .map(path => {
          return {
            lat: path.lat(),
            long: path.lng()
          };
        });
    } else {
      location.points = [];
    }
    location.pins = this.pins.map(value => {
      delete value.marker;
      return value;
    });

    if (this.customerLocation) {
      location.id = this.customerLocation.id;
    }

    location.customer =
      this.data && this.data.customer ? this.data.customer : null;

    if (this.oneTimeLocationControl.value) {
      location.customer = null;
    }

    this.dialogRef.close(location);
  }

  onMapReady(map) {
    this.map = map;
    this.initDrawingManager();
  }

  onClear() {
    if (this.selectedShape) {
      this.selectedShape.setMap(null);
      this.selectedShape = null;
    }
    this.pins.forEach(value => value.marker.setMap(null));
    this.pins = [];
    this.updateDrawingModesOption([this.POLYGON]);
    this.drawingManager.setDrawingMode(this.POLYGON);
  }

  private initDrawingManager() {
    this.drawingManager = new google.maps.drawing.DrawingManager(
      this.managerOptions
    );
    this.drawingManager.setMap(this.map);

    if (this.customerLocation) {
      this.drawInitialLocation();
    }

    google.maps.event.addListener(
      this.drawingManager,
      'polygoncomplete',
      event => {
        this.drawingManager.setDrawingMode(null);
        this.updateDrawingModesOption([]);
        this.selectedShape = event;
      }
    );

    google.maps.event.addListener(this.map, 'click', event => {
      this.ngZone.run(() => {
        this.onAddPin(event);
      });
    });
  }

  private drawInitialLocation() {
    const points = this.customerLocation.points.map(point => ({
      lat: point.lat,
      lng: point.long
    }));
    this.pins = this.getPins([...this.customerLocation.pins]);

    if (points.length > 0) {
      this.selectedShape = new google.maps.Polygon({
        paths: points,
        map: this.map,
        ...this.managerOptions.polygonOptions
      });
    }

    if (this.selectedShape) {
      this.updateDrawingModesOption([]);
      this.drawingManager.setDrawingMode(null);
    } else {
      this.updateDrawingModesOption([this.POLYGON]);
      this.drawingManager.setDrawingMode(null);
    }

    this.inputForm.setValue(this.customerLocation.name);
  }

  private updateDrawingModesOption(modes: string[]) {
    this.drawingManager.setOptions({
      drawingControlOptions: {
        drawingModes: modes
      }
    });
  }

  private onAddPin(event: any) {
    const pin = new LocationPin();
    pin.lat = event.latLng.lat();
    pin.long = event.latLng.lng();
    pin.primary = this.pins.length === 0;
    pin.marker = this.getMarker(pin, this.pins.length);
    pin.description = '';

    this.addDragEndListener(pin);
    this.pins = [...this.pins, pin];
  }

  private updatePins() {
    this.pins = this.pins.map((value, index) => {
      const pin = {
        ...value,
        marker: this.getMarker(value, index)
      };
      this.addDragEndListener(pin);

      return pin;
    });
  }

  private getPins(pins: LocationPin[]) {
    return pins.map((value, index) => {
      const pin = {
        ...value,
        marker: this.getMarker(value, index)
      };
      this.addDragEndListener(pin);

      return pin;
    });
  }

  private addDragEndListener(pin: LocationPin) {
    pin.marker.addListener('dragend', event => {
      this.ngZone.run(() => {
        pin.lat = event.latLng.lat();
        pin.long = event.latLng.lng();
      });
    });
  }

  private getMarker(pin: LocationPin, index: number) {
    const color = pin.primary ? 'green' : 'yellow';
    return new google.maps.Marker({
      position: {
        lat: pin.lat,
        lng: pin.long
      },
      label: `${index + 1}`,
      map: this.map,
      icon: this.pinSymbol(color),
      draggable: !this.disable
    });
  }

  private pinSymbol(color) {
    return {
      path:
        'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z',
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#000',
      strokeWeight: 1,
      scale: 1.4,
      labelOrigin: new google.maps.Point(0, -29)
    };
  }
}
